import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { acceptInvitation, getInvitationPublic } from '@/shared/api/invitations';
import { fmtKst } from '@/shared/lib/date';
import { useAuthStore, useIsAuthenticated } from '@/shared/auth/store';

function extractErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 410) return 'EXPIRED';
    if (status === 404) return 'NOT_FOUND';
    if (status === 409) return 'USED';
    if (status === 403) return 'REVOKED';
    const payload = err.response?.data as { error?: { code?: string } } | undefined;
    return payload?.error?.code ?? null;
  }
  return null;
}

export default function InvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const isAuth = useIsAuthenticated();
  const setPendingInviteToken = useAuthStore((s) => s.setPendingInviteToken);

  const inviteQ = useQuery({
    queryKey: ['invitation-public', token],
    queryFn: () => getInvitationPublic(token),
    enabled: !!token,
    retry: false,
  });

  const acceptMut = useMutation({
    mutationFn: () => acceptInvitation(token),
    onSuccess: (result) => {
      navigate(`/meetings/${result.meetingId}`, { replace: true });
    },
  });

  useEffect(() => {
    if (acceptMut.isSuccess || acceptMut.isError) {
      setPendingInviteToken(null);
    }
  }, [acceptMut.isSuccess, acceptMut.isError, setPendingInviteToken]);


  if (!token) {
    return (
      <section className="flex min-h-[calc(100vh-96px)] flex-col px-1 py-6">
        <div className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-5">
          <h1 className="text-[20px] font-semibold tracking-[-0.03em] text-ink">잘못된 링크</h1>
          <p className="mt-2 text-[13px] leading-5 text-ink-muted">초대 토큰이 없어요.</p>
        </div>
      </section>
    );
  }

  if (inviteQ.isLoading) {
    return (
      <section className="flex min-h-[calc(100vh-96px)] flex-col px-1 py-6">
        <div className="rounded-[22px] bg-hero p-5 text-white shadow-[var(--hero-glow)]">
          <p className="text-[11px] font-semibold text-white/75">초대장</p>
          <p className="mt-3 text-[22px] font-semibold tracking-[-0.04em]">초대 정보를 불러오는 중</p>
          <div className="mt-5 h-2 rounded-full bg-white/15">
            <div className="h-2 w-1/2 rounded-full bg-white/60" />
          </div>
        </div>
      </section>
    );
  }

  if (inviteQ.isError) {
    const code = extractErrorCode(inviteQ.error);
    let message = '초대 링크를 불러오지 못했어요.';
    if (code === 'EXPIRED') message = '링크가 만료됐어요. 모임장에게 새 링크를 요청해주세요.';
    else if (code === 'REVOKED') message = '취소된 초대 링크예요. 모임장에게 새 링크를 요청해주세요.';
    else if (code === 'USED' || code === 'ALREADY_TERMINAL')
      message = '이미 사용된 초대 링크예요.';
    else if (code === 'NOT_FOUND') message = '존재하지 않는 초대 링크예요.';
    return (
      <section className="flex min-h-[calc(100vh-96px)] flex-col px-1 py-6">
        <div className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-5">
          <h1 className="text-[20px] font-semibold tracking-[-0.03em] text-ink">초대 확인 불가</h1>
          <p role="alert" className="mt-3 text-[13px] leading-5 text-ink-muted">
            {message}
          </p>
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="mt-5 h-11 w-full rounded-[14px] bg-canvas text-[13px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            홈으로
          </button>
        </div>
      </section>
    );
  }

  const inv = inviteQ.data!;
  const hostName = inv.hostNickname || '알 수 없음';
  const hostInitial = hostName.trim().slice(0, 1) || '모';
  const memberInitials = [hostInitial, '이', '박'].slice(0, Math.max(inv.participantCount, 1));
  if (inv.participantCount > 3) memberInitials.push(`+${inv.participantCount - 3}`);
  const regionText = inv.meetingRegion ?? '지역 미정';
  const dateText = inv.confirmedDate ? fmtKst(inv.confirmedDate, 'M월 d일 (E) HH:mm') : '날짜 조율 중';
  const pointText = inv.meetingPointName ?? '장소 조율 중';

  function onLoginFirst() {
    setPendingInviteToken(token);
    navigate('/login', { state: { from: { pathname: `/invite/${token}` } } });
  }

  function onJoin() {
    acceptMut.mutate();
  }

  const acceptError = acceptMut.isError ? extractErrorCode(acceptMut.error) : null;
  const acceptErrorMessage =
    acceptError === 'EXPIRED'
      ? '링크가 만료됐어요. 모임장에게 새 링크를 요청해주세요.'
      : acceptError === 'USED'
        ? '이미 사용된 초대 링크예요. 새 링크를 요청해주세요.'
        : acceptError === 'REVOKED'
          ? '취소된 초대 링크예요. 새 링크를 요청해주세요.'
          : acceptError === 'NOT_FOUND'
            ? '초대 링크를 찾을 수 없어요.'
            : acceptError
              ? '합류할 수 없는 링크예요. 모임장에게 새 링크를 요청해주세요.'
              : null;

  return (
    <section className="flex min-h-[calc(100vh-96px)] flex-col px-1 py-5">
      <div className="relative mb-4 overflow-hidden rounded-[22px] bg-hero p-5 text-white shadow-[var(--hero-glow)]">
        <div
          className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10"
          aria-hidden="true"
        />
        <p className="text-[11px] font-semibold text-white/75">초대장</p>
        <h1 className="mt-2.5 text-[23px] font-semibold leading-[1.16] tracking-[-0.04em]">
          {inv.meetingTitle}에
          <br />
          초대받았어요
        </h1>
        <p className="mt-2 text-[12px] leading-5 text-white/70">
          친구들과 날짜, 장소, 정산까지 함께 정해요.
        </p>
      </div>

      <div className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-[42px] w-[42px] items-center justify-center rounded-[14px] bg-primary text-[15px] font-semibold text-white">
            {hostInitial}
          </div>
          <div>
            <p className="text-[14px] font-semibold text-ink">{hostName}님의 초대</p>
            <p className="mt-1 text-[11px] text-ink-disabled">모임 초대 링크</p>
          </div>
        </div>

        <div className="mt-3.5 rounded-[15px] bg-canvas p-3.5">
          <p className="text-[17px] font-semibold tracking-[-0.03em] text-ink">
            {inv.meetingTitle}
          </p>
          <p className="mt-1.5 text-[12px] leading-5 text-ink-muted">
            {inv.participantCount}명 참여 · {regionText}
          </p>
          <div className="mt-3 grid gap-2">
            <div className="rounded-[12px] bg-surface px-3 py-2">
              <p className="text-[10px] font-semibold text-ink-faint">언제</p>
              <p className="mt-0.5 text-[13px] font-semibold text-ink">{dateText}</p>
            </div>
            <div className="rounded-[12px] bg-surface px-3 py-2">
              <p className="text-[10px] font-semibold text-ink-faint">어디서</p>
              <p className="mt-0.5 truncate text-[13px] font-semibold text-ink">{pointText}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            {memberInitials.map((initial, index) => (
              <span
                key={`${initial}-${index}`}
                className={`flex h-[26px] w-[26px] items-center justify-center rounded-full border-2 border-canvas text-[9px] font-semibold text-white ${
                  index === 0
                    ? 'bg-primary'
                    : index === 1
                      ? 'bg-success'
                      : index === 2
                        ? 'bg-warning'
                        : 'bg-[#d9a03f]'
                } ${index > 0 ? '-ml-1.5' : ''}`}
              >
                {initial}
              </span>
            ))}
            <span className="ml-2 text-[11px] text-ink-disabled">
              {inv.participantCount}명 참여 중
            </span>
          </div>
        </div>

        {acceptErrorMessage && (
          <p role="alert" className="mt-3 text-small font-medium text-error">
            {acceptErrorMessage}
          </p>
        )}

        {isAuth ? (
          <>
            <button
              type="button"
              onClick={onJoin}
              disabled={acceptMut.isPending}
              className="mt-3.5 h-[46px] w-full rounded-[14px] bg-primary text-[14px] font-semibold text-white disabled:bg-primary-light/70 focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              {acceptMut.isPending ? '수락 중…' : '합류하기'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="mt-2 h-11 w-full rounded-[14px] bg-canvas text-[13px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              모이자 둘러보기
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onLoginFirst}
              className="mt-3.5 h-[46px] w-full rounded-[14px] bg-primary text-[14px] font-semibold text-white focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              로그인하고 합류하기
            </button>
            <p className="mt-2 text-center text-[11px] leading-4 text-ink-muted">
              처음이어도 괜찮아요 — 3초 로그인 뒤 이 초대가 자동으로 이어져요.
            </p>
          </>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2.5 text-[12px] text-ink-muted">
          <span className="flex h-7 w-7 items-center justify-center rounded-[10px] bg-primary-soft text-primary">
            <svg aria-hidden="true" width="13" height="13" viewBox="0 0 20 20" fill="none">
              <path d="M4.5 10.5L8.2 14L15.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span>수락하면 모임에 바로 참여해요</span>
        </div>
        <div className="flex items-center gap-2.5 text-[12px] text-ink-muted">
          <span className="flex h-7 w-7 items-center justify-center rounded-[10px] bg-primary-soft text-primary">
            <svg aria-hidden="true" width="13" height="13" viewBox="0 0 20 20" fill="none">
              <rect x="5" y="9" width="10" height="7" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
              <path d="M7.5 9V6.8C7.5 5.2 8.5 4.2 10 4.2C11.5 4.2 12.5 5.2 12.5 6.8V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </span>
          <span>초대 링크가 있는 사람만 들어올 수 있어요</span>
        </div>
      </div>

      <p className="mt-auto pt-6 text-center text-[10px] leading-4 text-ink-disabled">
        계속하면 모이자 이용약관과 개인정보 처리방침에 동의합니다.
      </p>
    </section>
  );
}
